import React, { createContext, useContext, useState } from 'react';

const QuizContext = createContext();

export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};

export const QuizProvider = ({ children }) => {
  // Company entfernt, nur Name und Email
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const smsNumber = "+4915560051112";
  const smsText = encodeURIComponent("Hallo Eva, ich hab das Quizz gemacht und möchte gern ein kennenlerngespräch buchen.");
  const smsHref = `sms:${smsNumber}?body=${smsText}`;

  // Fragen bleiben identisch
  const questions = [
    {
      id: 1,
      question: "Dein Kalender ist voll, es kommt noch was rein. Du reagierst meistens so:",
      options: [
        { text: "Ich schiebe es rein. Wird schon gehen. Notfalls weniger Schlaf.", type: "A" },
        { text: "Ich sage „okay“, obwohl ich innerlich schon genervt bin.", type: "B" },
        { text: "Ich sage spontan zu – und merke später, dass es Quatsch war.", type: "C" },
        { text: "Ich mache sofort einen Plan, wie ich alles unter Kontrolle kriege.", type: "D" },
        { text: "Ich checke kurz Ressourcen und entscheide bewusst: ja oder nein.", type: "E" }
      ]
    },
    {
      id: 2,
      question: "Du merkst: Erkältung im Anflug / Körper ist gereizt. Was machst du typischerweise?",
      options: [
        { text: "Egal. Durchziehen. „Das muss fertig werden.“", type: "A" },
        { text: "Ich will niemanden hängen lassen und mach weiter.", type: "B" },
        { text: "Ich merke es erst richtig, wenn’s mich umhaut.", type: "C" },
        { text: "Ich recherchiere alles, optimiere alles – und werde dabei eher unruhiger.", type: "D" },
        { text: "Ich reduziere konsequent, ohne Drama, und beobachte die Marker.", type: "E" }
      ]
    },
    {
      id: 3,
      question: "Konflikt-Situation (Partner, Chef, Arzt): du willst etwas anderes als die andere Person.",
      options: [
        { text: "Ich halte dagegen und ziehe es durch – auch wenn ich überdrehe.", type: "A" },
        { text: "Ich gebe nach. Hauptsache Ruhe. Später ärgere ich mich.", type: "B" },
        { text: "Ich werde innerlich leer oder verwirrt – und „bin plötzlich nicht mehr richtig da“.", type: "C" },
        { text: "Ich argumentiere sachlich, kontrolliere das Gespräch und lasse wenig Spielraum.", type: "D" },
        { text: "Ich bleibe klar, sage meinen Punkt, und halte die Spannung aus.", type: "E" }
      ]
    },
    {
      id: 4,
      question: "Nach einem Gespräch, das dich triggert (Befund, Kritik, Streit): Was passiert am ehesten?",
      options: [
        { text: "Ich werde noch aktiver: Sport, Arbeit, Aktionismus.", type: "A" },
        { text: "Ich schlucke’s runter und funktioniere weiter.", type: "B" },
        { text: "Später kommt’s als Symptom oder Totalausfall.", type: "C" },
        { text: "Ich zerdenke alles stundenlang (Plan A–D).", type: "D" },
        { text: "Ich merke es, reguliere mich und komme wieder runter.", type: "E" }
      ]
    },
    {
      id: 5,
      question: "Wenn du „Nein“ sagen müsstest (Termin absagen, Grenze setzen):",
      options: [
        { text: "Ich sage zu und mache es irgendwie möglich.", type: "A" },
        { text: "Ich sage ja, weil ich niemanden enttäuschen will.", type: "B" },
        { text: "Ich sage impulsiv ja – und kriege später Panik, wie ich das schaffen soll.", type: "C" },
        { text: "Ich formuliere zehn Varianten, um jedes Risiko zu vermeiden – und verschiebe es.", type: "D" },
        { text: "Ich sage nein (kurz, klar) und halte das schlechte Gefühl aus.", type: "E" }
      ]
    },
    {
      id: 6,
      question: "Du wachst nachts auf (Grübeln/Unruhe). Was ist dein Standardmuster?",
      options: [
        { text: "Kopf an: To-do-Liste, Lösungen, weiter planen.", type: "A" },
        { text: "Ich denke an andere: „Wie krieg ich das hin, dass es für alle passt?“", type: "B" },
        { text: "Ich bin wie benebelt, kann nicht klar denken, fühle mich „weg“.", type: "C" },
        { text: "Ich analysiere Werte, Symptome, Ursachen, Szenarien.", type: "D" },
        { text: "Ich kann mich runterregulieren (Atem, Körper, Routine) und schlafe wieder ein.", type: "E" }
      ]
    },
    {
      id: 7,
      question: "Du bekommst neue Informationen (Labor, Symptome, „was stimmt nicht“):",
      options: [
        { text: "Ich ignoriere es erstmal und mache weiter.", type: "A" },
        { text: "Ich bleibe freundlich und angepasst, auch wenn ich Fragen habe.", type: "B" },
        { text: "Ich bin kurz wie blockiert, kriege es nicht sortiert, schiebe es weg.", type: "C" },
        { text: "Ich gehe in Kontrolle: lesen, vergleichen, alles verstehen wollen.", type: "D" },
        { text: "Ich entscheide: Was ist heute der nächste sinnvolle Schritt?", type: "E" }
      ]
    },
    {
      id: 8,
      question: "Wenn dein Körper früh „piepst“ (Kiefer, Haut, Herzklopfen, Druck):",
      options: [
        { text: "Ich stelle den Alarm aus und mache weiter.", type: "A" },
        { text: "Ich übergehe es, damit ich keinen Stress mit anderen habe.", type: "B" },
        { text: "Ich merke es kaum – bis es laut wird.", type: "C" },
        { text: "Ich überwache es ständig und werde dadurch noch angespannter.", type: "D" },
        { text: "Ich nehme es ernst und reagiere früh (kleiner Hebel statt großer Crash).", type: "E" }
      ]
    },
    {
      id: 9,
      question: "Jemand macht etwas „nicht richtig“ (Team, Familie). Was passiert in dir?",
      options: [
        { text: "Ich übernehme. Dann ist es wenigstens erledigt.", type: "A" },
        { text: "Ich helfe sofort, damit keiner schlecht dasteht.", type: "B" },
        { text: "Ich sage „passt schon“ – und später bin ich genervt oder platt.", type: "C" },
        { text: "Ich kontrolliere nach, korrigiere, gebe Vorgaben.", type: "D" },
        { text: "Ich lasse Spielraum, setze Grenzen, und akzeptiere 80% wenn es reicht.", type: "E" }
      ]
    },
    {
      id: 10,
      question: "Du hast mehrere Baustellen gleichzeitig (Arbeit, Familie, Gesundheit). Dein Muster:",
      options: [
        { text: "Mehr Tempo. Noch früher aufstehen. Noch mehr leisten.", type: "A" },
        { text: "Ich halte aus und trage still die Last.", type: "B" },
        { text: "Ich verliere den Überblick, verspreche zu viel, werde inkonsistent.", type: "C" },
        { text: "Ich baue Systeme/Listen/Pläne, aber werde innerlich enger.", type: "D" },
        { text: "Ich priorisiere brutal: was zählt heute wirklich, was kann weg.", type: "E" }
      ]
    },
    {
      id: 11,
      question: "Du bist reizüberflutet (Lärm, Menschen, Dauerinfo):",
      options: [
        { text: "Ich presse mich durch. „Nicht so anstellen.“", type: "A" },
        { text: "Ich bleibe freundlich, obwohl ich eigentlich raus will.", type: "B" },
        { text: "Ich werde wie taub/abwesend oder mache dicht.", type: "C" },
        { text: "Ich versuche alles zu kontrollieren: Rückzug, Struktur, Regeln – sofort.", type: "D" },
        { text: "Ich steuere rechtzeitig gegen (Pause, runterfahren, Grenzen).", type: "E" }
      ]
    },
    {
      id: 12,
      question: "Thema IgA / Gesundheit: Wie nutzt du Wissen?",
      options: [
        { text: "Ich weiß vieles, aber ignoriere es im Alltag, wenn’s unbequem wird.", type: "A" },
        { text: "Ich richte mich nach dem, was andere erwarten („das macht man so“).", type: "B" },
        { text: "Ich verliere mich: mal alles, mal nichts – ohne klare Linie.", type: "C" },
        { text: "Ich sammle Wissen, perfektioniere – Umsetzung bleibt zäh.", type: "D" },
        { text: "Ich setze wenige Dinge konsequent um und überprüfe den Effekt.", type: "E" }
      ]
    },
    {
      id: 13,
      question: "Kurzfristig kommt eine Extra-Aufgabe rein (Mail/Anruf). Du:",
      options: [
        { text: "nimmst sie sofort an und schiebst alles andere nach hinten.", type: "A" },
        { text: "sagst „klar“ – auch wenn du eigentlich schon überläufst.", type: "B" },
        { text: "sagst impulsiv zu und merkst später: war unrealistisch.", type: "C" },
        { text: "sortierst sofort neu, baust einen Plan und kontrollierst alles nach.", type: "D" },
        { text: "prüfst kurz: Kapazität/Prio – und entscheidest dann bewusst.", type: "E" }
      ]
    },
    {
      id: 14,
      question: "Pausen im Alltag:",
      options: [
        { text: "Pausen sind Luxus. Erst wenn alles fertig ist.", type: "A" },
        { text: "Ich mache Pause, wenn andere es „erlauben“ oder keiner was braucht.", type: "B" },
        { text: "Ich vergesse Pausen komplett – bis mein Körper dichtmacht.", type: "C" },
        { text: "Ich plane Pausen, aber bin innerlich dabei trotzdem im Kontrollmodus.", type: "D" },
        { text: "Ich mache Pausen früh, klein und regelmäßig.", type: "E" }
      ]
    },
    {
      id: 15,
      question: "Jemand will etwas von dir (Partner, Team, Familie), du bist aber schon am Limit:",
      options: [
        { text: "Ich mache es trotzdem. Zähne zusammenbeißen.", type: "A" },
        { text: "Ich mache es, um keinen Stress zu haben – und ärgere mich später.", type: "B" },
        { text: "Ich sage erst ja und „verschwinde“ dann mental / ziehe mich zurück.", type: "C" },
        { text: "Ich übernehme und lege fest, wie es gemacht wird.", type: "D" },
        { text: "Ich setze eine klare Grenze oder mache ein realistisches Angebot.", type: "E" }
      ]
    },
    {
      id: 16,
      question: "Arztgespräch: Du verstehst etwas nicht / hast Fragen:",
      options: [
        { text: "Ich drücke durch, will schnell fertig werden, kläre es später irgendwie.", type: "A" },
        { text: "Ich nicke, will nicht nerven – und bin danach unzufrieden.", type: "B" },
        { text: "Ich bin innerlich wie blockiert und kriege keine Frage raus.", type: "C" },
        { text: "Ich komme mit Liste, will alles absichern, werde dabei eher angespannter.", type: "D" },
        { text: "Ich frage nach, bis es klar ist – ohne mich zu entschuldigen.", type: "E" }
      ]
    },
    {
      id: 17,
      question: "Körper meldet Schmerz/Entzündung an:",
      options: [
        { text: "Ich ignoriere es und mache weiter, bis es richtig knallt.", type: "A" },
        { text: "Ich halte es aus, weil „ich kann jetzt keinen Ärger machen“.", type: "B" },
        { text: "Ich merke es lange kaum – dann kommt der Einbruch plötzlich.", type: "C" },
        { text: "Ich beobachte/messe/analysiere alles und komme nicht in Ruhe.", type: "D" },
        { text: "Ich reagiere früh mit einem kleinen, konkreten Hebel (runterfahren).", type: "E" }
      ]
    },
    {
      id: 18,
      question: "Du hast dich übernommen und musst absagen / umplanen:",
      options: [
        { text: "Ich versuche es trotzdem irgendwie möglich zu machen.", type: "A" },
        { text: "Ich sage ungern ab, hab Schuldgefühl, rette es auf meine Kosten.", type: "B" },
        { text: "Ich schiebe es vor mir her und bin wie gelähmt.", type: "C" },
        { text: "Ich schreibe lange Erklärungen, plane Varianten, um keinen Konflikt zu haben.", type: "D" },
        { text: "Ich sage kurz ab, ohne Roman, und halte die Spannung aus.", type: "E" }
      ]
    },
    {
      id: 19,
      question: "Abends: du bist müde. Was machst du am ehesten?",
      options: [
        { text: "Noch schnell was erledigen. Dann ist’s wenigstens weg.", type: "A" },
        { text: "Ich kümmere mich erst um alle anderen, dann falle ich um.", type: "B" },
        { text: "Ich sitze da, scrolle, bin wie betäubt – und schlafe schlecht.", type: "C" },
        { text: "Ich plane den nächsten Tag durch, damit nichts passiert.", type: "D" },
        { text: "Ich mache bewusst runter (Routine) und komme in echten Schlafmodus.", type: "E" }
      ]
    },
    {
      id: 20,
      question: "Wochenende/Einladung/Projekt klingt spannend:",
      options: [
        { text: "Ich packe es rein. Wird schon.", type: "A" },
        { text: "Ich sage ja, weil es erwartet wird oder „man sagt da nicht nein“.", type: "B" },
        { text: "Ich sage spontan zu – und merke später: Terminkollisionen, Stress.", type: "C" },
        { text: "Ich prüfe alles, plane alles, überlege hundert Faktoren – und bin unruhig.", type: "D" },
        { text: "Ich entscheide nach Energie + Priorität, nicht nach FOMO.", type: "E" }
      ]
    },
    {
      id: 21,
      question: "Wenn Dinge unklar sind (Warten auf Befund/Antwort/Entscheidung):",
      options: [
        { text: "Ich werde ungeduldig und mache Aktionismus.", type: "A" },
        { text: "Ich spiele nach außen ruhig, innen frisst es mich.", type: "B" },
        { text: "Ich schalte innerlich ab, verdränge – und später kommt Symptomdruck.", type: "C" },
        { text: "Ich recherchiere mich verrückt und baue Worst-Case-Szenarien.", type: "D" },
        { text: "Ich definiere: Was ist heute beeinflussbar – und bleibe bei dem.", type: "E" }
      ]
    },
    {
      id: 22,
      question: "Vergleich mit anderen (Kollegen, Social Media, „die schaffen das doch auch“):",
      options: [
        { text: "Ich trete aufs Gas, um mitzuhalten.", type: "A" },
        { text: "Ich passe mich an und verliere dabei mich selbst.", type: "B" },
        { text: "Ich werde diffus, verliere Fokus, mache dann gar nichts klar.", type: "C" },
        { text: "Ich optimiere und kontrolliere, statt einfach umzusetzen.", type: "D" },
        { text: "Ich vergleiche mich nur mit mir und bleibe bei meinem Maß.", type: "E" }
      ]
    },
    {
      id: 23,
      question: "Wenn du einen Fehler machst oder etwas nicht klappt:",
      options: [
        { text: "Ich kompensiere mit Mehrleistung.", type: "A" },
        { text: "Ich entschuldige mich zu viel und versuche es „wieder gut zu machen“.", type: "B" },
        { text: "Ich mache innerlich zu / werde taub / schiebe es weg.", type: "C" },
        { text: "Ich analysiere es bis zum Umfallen und baue neue Regeln/Listen.", type: "D" },
        { text: "Ich korrigiere pragmatisch und gehe weiter.", type: "E" }
      ]
    },
    {
      id: 24,
      question: "Frühmarker: woran merkst du Stress am ehesten?",
      options: [
        { text: "Ich merke es – aber ignoriere es (Kiefer, Schlaf, Druck).", type: "A" },
        { text: "Ich merke es, aber bleibe angepasst und halte durch.", type: "B" },
        { text: "Ich merke es kaum – bis Tinnitus/Schwindel/Crash kommt.", type: "C" },
        { text: "Ich merke es und versuche sofort alles zu kontrollieren/zu messen.", type: "D" },
        { text: "Ich merke es früh und reguliere rechtzeitig.", type: "E" }
      ]
    }
  ];

  const typeContent = {
    A: {
      id: 'uebersteuerer',
      resultTitle: "Übersteuerer",
      title: "Übersteuerer: Du hältst durch – bis der Körper die Handbremse zieht.",
      description: "Du bist zuverlässig, schnell, leistungsfähig. Und genau deshalb rutschst du über deine Grenzen, ohne es zu merken. Dein System fährt nicht langsam runter. Es knallt. Entzündung, Schmerz, Infekt, Crash – das ist oft nicht „Pech“, sondern Überlastung mit Anlauf.",
      signs: [
        "Du machst weiter, obwohl du längst merkst, dass es zu viel ist.",
        "Krankheit/Schmerz ist erst dann „echt“, wenn du nicht mehr stehen kannst.",
        "Du glaubst, du musst liefern, sonst fällt alles auseinander."
      ],
      levers: [
        "80/20 statt 110%: „Reicht“ ist ab heute ein Standard.",
        "Kalender-Deckel: Nicht mehr Aufgaben pro Tag, als du am schlechtesten Tag packst.",
        "Stopp-Signal ernst nehmen: Schlaf bricht, Kiefer knallt, Gelenk muckt = runterfahren."
      ],
      reset: "Setz dich hin. Schultern runter. Ausatmen länger als einatmen (z.B. 4s ein, 6s aus) – 6 Runden. Das ist der Schalter: „Gefahr vorbei“.",
      nextStep: "Wähle eine Sache, die du diese Woche nicht machst. Nicht später. Nicht „wenn Zeit ist“. Weg.",
      cta: [
        { text: "Kennenlerngespräch (SMS)", sub: "Klick hier für eine direkte SMS-Anfrage.", href: smsHref },
        { text: "12 Wochen Begleitung", sub: "Für Leute, die erst dann bremsen, wenn’s raucht." }
      ],
      color: "text-red-600",
      bgColor: "bg-red-50"
    },
    B: {
      id: 'anpasser',
      resultTitle: "Anpasser",
      title: "Anpasser: Du vermeidest Stress mit Menschen – und bekommst Stress im Körper.",
      description: "Du kannst Konflikte umgehen wie ein Profi. Nach außen ruhig, innen druckvoll. Du sagst „passt schon“, obwohl es nicht passt. Der Körper übernimmt dann die Ansage: Druck, Haut, Schlaf, Entzündung – irgendwas findet einen Ausgang.",
      signs: [
        "Du sagst ja, um niemanden zu enttäuschen.",
        "Du ärgerst dich später, aber im Moment „geht’s nicht“.",
        "Du erklärst dich viel, statt klar zu entscheiden."
      ],
      levers: [
        "Nein ohne Roman: „Heute nicht.“ Punkt.",
        "Schuldgefühl ist Trainingsgewicht: unangenehm, aber normal.",
        "Ansage statt Abgabe: Du entscheidest, nicht die Situation."
      ],
      reset: "Hand aufs Brustbein. Einmal tief ausatmen. Sag innerlich: „Ich darf mich kurz unpopulär machen.“ Dann erst antworten.",
      nextStep: "Diese Woche ein Nein ohne Begründung. Freundlich. Kurz. Fertig.",
      cta: [
        { text: "Kennenlerngespräch (SMS)", sub: "Klick hier für eine direkte SMS-Anfrage.", href: smsHref },
        { text: "Trainingsgruppe", sub: "Hier übst du das, was du alleine immer wieder umgehst." }
      ],
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    C: {
      id: 'abgekoppelt',
      resultTitle: "Abgekoppelt",
      title: "Abgekoppelt: Du merkst das Zuviel zu spät – dann kommt der Totalausfall.",
      description: "Dein System ist nicht „zu sensibel“. Es ist schlau – nur auf eine harte Weise: Es schaltet ab, wenn’s zu viel wird. Du funktionierst weiter, aber die Wahrnehmung ist gedimmt. Und irgendwann steht der Körper im Türrahmen und sagt: Feierabend.",
      signs: [
        "Du sagst zu schnell zu und bereust es später.",
        "Du merkst Hunger, Müdigkeit oder Stress erst, wenn’s kippt.",
        "Du wirst in Zwickmühlen „leer“ statt klar."
      ],
      levers: [
        "Entscheidungen verzögern: „Ich melde mich später“ ist ein Skill.",
        "Körper-Check 3× täglich: Kiefer, Brust, Bauch, Beine: Spannung 0–10.",
        "Klarheit vor Harmonie: Lieber kurz enttäuschen als später crashen."
      ],
      reset: "Füße fest in den Boden. Blick im Raum wandern lassen (5 Dinge sehen). Lang ausatmen. Sag: „Ich bin hier. Ich entscheide später.“",
      nextStep: "Ab heute: keine Zusage ohne Kalendercheck + 10 Sekunden Körpercheck.",
      cta: [
        { text: "Kennenlerngespräch (SMS)", sub: "Klick hier für eine direkte SMS-Anfrage.", href: smsHref },
        { text: "12 Wochen Begleitung", sub: "Erst Stabilität, dann Veränderung." }
      ],
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    D: {
      id: 'kontrollierer',
      resultTitle: "Kontrollierer",
      title: "Kontrollierer: Du denkst dich sicher – aber der Körper bleibt im Alarm.",
      description: "Du bist stark im Kopf: planen, verstehen, absichern. Das ist hilfreich – bis es dich dauernd unter Strom hält. Wenn du immer alles im Blick hast, kann dein Nervensystem nie „Feierabend“ melden. Und irgendwann kommt’s körperlich zurück.",
      signs: [
        "Du machst Listen, Pläne, Optionen – und wirst trotzdem nicht ruhiger.",
        "Du erwartest von dir und anderen hohe Genauigkeit.",
        "Du recherchiere viel, setzt wenig konsequent um."
      ],
      levers: [
        "Wissen → 1 Handlung pro Tag (kein zusätzlicher Input bis umgesetzt).",
        "Plan-Stop: 10 Minuten planen, dann ausführen.",
        "Körper runterfahren statt Kopf füttern: PMR / Atem / Bewegung."
      ],
      reset: "Stell dir vor: Du stellst einen Schieberegler von „Kontrolle“ auf „genug“. Einmal lang ausatmen. Sag: „Heute reicht ein Hebel.“",
      nextStep: "Wähle einen Hebel für 7 Tage. Tracke nur den. Nicht zehn Dinge.",
      cta: [
        { text: "Kennenlerngespräch (SMS)", sub: "Klick hier für eine direkte SMS-Anfrage.", href: smsHref },
        { text: "Selbstlern-Kurs", sub: "Für kluge Köpfe, die ins Tun kommen wollen." }
      ],
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    E: {
      id: 'stabil',
      resultTitle: "Stabil gerade",
      title: "Stabil gerade: Dein System ist aktuell okay – halte es so.",
      description: "Gute Nachricht: Gerade ist kein Feuer. Aber du hast eine chronische Baustelle (IgA-Kontext), und Stabilität ist kein Charakter, sondern Wartung. Du brauchst keinen großen Umbau – du brauchst einen klaren Plan.",
      signs: [
        "Du merkst Stress früh genug und kannst gegensteuern.",
        "Schlaf ist nicht perfekt, aber er trägt.",
        "Du setzt Grenzen zumindest oft genug."
      ],
      levers: [
        "Top-3 Frühmarker (deine persönlichen Warnlampen).",
        "Top-1 Gegenmaßnahme (klein, machbar, sofort).",
        "Stress-Spitzen abfedern (Pausen bevor es knallt)."
      ],
      reset: "Einmal lang ausatmen. Frage: „Was ist heute der kleinste Schritt, der Stabilität erhält?“ Dann tu genau den.",
      nextStep: "Schreib dir auf: 3 Frühmarker + 1 Gegenmaßnahme. Pack’s als Notiz aufs Handy.",
      cta: [
        { text: "Kennenlerngespräch (SMS)", sub: "Klick hier für eine direkte SMS-Anfrage.", href: smsHref },
        { text: "Quiz weiterleiten", sub: "Kennst du jemanden, bei dem’s ständig kippt?" }
      ],
      color: "text-green-600",
      bgColor: "bg-green-50"
    }
  };

  const getRecommendation = () => {
    const counts = { A: 0, B: 0, C: 0, D: 0, E: 0 };
    Object.values(answers).forEach(ans => {
      counts[ans.type]++;
    });

    const sortedTypes = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const topType = sortedTypes[0][0];
    const secondType = sortedTypes[1][0];
    const topCount = sortedTypes[0][1];
    const secondCount = sortedTypes[1][1];

    if (counts.E >= 8 && counts[sortedTypes.find(t => t[0] !== 'E')[0]] <= 6) {
      return typeContent.E;
    }

    if (topCount - secondCount <= 1) {
      const main = typeContent[topType];
      const sub = typeContent[secondType];
      return {
        ...main,
        title: `${main.resultTitle} mit ${sub.resultTitle}-Tendenz`,
        isMixed: true
      };
    }

    return typeContent[topType];
  };

  const saveAnswer = (questionId, selectedOption) => {
    setAnswers(prev => ({ ...prev, [questionId]: selectedOption }));
  };

  const resetQuiz = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setQuizCompleted(false);
    setUserInfo({ name: '', email: '' });
  };

  return (
    <QuizContext.Provider value={{
      userInfo, setUserInfo, answers, saveAnswer,
      currentQuestion, setCurrentQuestion, questions,
      quizCompleted, setQuizCompleted, getRecommendation, resetQuiz
    }}>
      {children}
    </QuizContext.Provider>
  );
};